/**
 * src/components/reportUtils.ts
 * 日報関連の共通定数・ユーティリティ
 */

// ── 工程（「納品」を除外、「修正」「その他」を追加）──
export const STAGE_OPTIONS: { value: string; label: string }[] = [
  { value: 'name',          label: 'ネーム' },
  { value: 'name_revision', label: 'ネーム修正' },
  { value: 'sketch',        label: '下書き' },
  { value: 'inking',        label: 'ペン入れ' },
  { value: 'background',    label: '背景' },
  { value: 'finishing',     label: '仕上げ' },
  { value: 'revision',      label: '修正' },
  { value: 'other',         label: 'その他' },
]

export const STAGE_LABELS: Record<string, string> = Object.fromEntries(
  STAGE_OPTIONS.map(({ value, label }) => [value, label])
)
// 旧データ互換（submitted が保存されている場合の表示用）
STAGE_LABELS['submitted'] = '納品'

// ── カイカイキキ業務の識別子 ──
export const KAIKAI_KIKI_ID = '__kaikai__'
export const KAIKAI_KIKI_LABEL = 'カイカイキキ業務'

export function isKaikaiKiki(workId?: string | null, workTitle?: string | null): boolean {
  return workId === KAIKAI_KIKI_ID || workTitle === KAIKAI_KIKI_LABEL
}

// ── 画像URL正規化 ──
const WORKER_BASE = import.meta.env.VITE_API_BASE ?? 'https://mangaos.studio-nekonomiya.workers.dev/api'

export function normalizeImageUrl(raw: string): string {
  if (!raw) return ''
  if (raw.startsWith('http://') || raw.startsWith('https://')) return raw
  if (raw.startsWith('/api/upload/file/')) return `${WORKER_BASE}${raw}`
  return `${WORKER_BASE}/upload/file/${raw}`
}

export function parseImageUrls(raw: unknown): string[] {
  try {
    if (Array.isArray(raw)) return raw.map(String).filter(Boolean)
    if (typeof raw === 'string' && raw.startsWith('[')) return JSON.parse(raw)
    if (typeof raw === 'string' && raw) return [raw]
  } catch { /* ignore */ }
  return []
}

// ── 作業明細 ──
export type WorkDetail = {
  work_id?:         string
  work_title?:      string
  episode_number?:  number | string
  stage?:           string
  pages_done?:      number | string
  memo?:            string
  // 作業データ保存先（NASの共有フォルダパスなど）。作品・作業行ごとに持たせる。
  // Windows/Mac双方の表記（\\... / smb://... / 相対パス）をそのまま保存し、正規化はしない。
  work_data_path?:  string
}

export function parseWorkDetails(raw: unknown): WorkDetail[] | null {
  try {
    if (Array.isArray(raw)) return raw
    if (typeof raw === 'string' && raw.startsWith('[')) return JSON.parse(raw)
  } catch { /* ignore */ }
  return null
}

export function legacyToDetail(report: Record<string, unknown>): WorkDetail[] {
  return [{
    work_title:     String(report.work_title ?? ''),
    episode_number: report.episode_number != null ? String(report.episode_number) : '',
    stage:          String(report.stage ?? ''),
    pages_done:     report.pages_done != null ? Number(report.pages_done) : '',
    memo:           String(report.description ?? ''),
  }]
}

export function totalPages(details: WorkDetail[]): number {
  return details.reduce((s, d) => s + (Number(d.pages_done) || 0), 0)
}

export function emptyDetail(): WorkDetail {
  return { work_id: '', work_title: '', episode_number: '', stage: 'background', pages_done: '', memo: '', work_data_path: '' }
}